import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from '../../src/Context/AuthContext';
import USStatesDropdown from '../../Components/Shared/USStatesDropdown';
import Modal from '../../Components/Shared/Modal';
import AuthHeader from '../../Components/Account/AuthHeader';

const RegisterEntityPage = ({ portalSettings }) => {
    //State Variables
    const { login, isAuthenticated } = useAuth();
    const [formData, setFormData] = useState({
        polNo: "",
        entityName: '',
        state: '',
        zip: '',
        fullTIN: '',
        userName: '',
        email: '',
        confirmEmail: '',
        password: '',
        confirmPassword: ''
    });

    const [modalData, setModalData] = useState({
        show: false,
        message: "",
        onAction: null,
    });

    const navigate = useNavigate();

    //ENV Variables
    const companyName = import.meta.env.VITE_COMPANY_NAME;
    const apiURL = import.meta.env.VITE_API;

    // If loading auth or no portal settings yet, just render nothing
    if (!portalSettings) return null;

    const psm = portalSettings || {};

    //Set Site Title
    document.title = `${companyName} - Register Entity`;

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/home');
        }
        if (psm.hasPolicyPortal == false) {
            if (psm.hasAgentPortal == true) {
                navigate('/registerAgent');
            }
            else {
                navigate('/login');
            }
        }
    }, [isAuthenticated, navigate]);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (formData.email !== formData.confirmEmail) {
            setModalData({
                show: true,
                message: "Emails do not match",
                onAction: null,
            });
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setModalData({
                show: true,
                message: "Passwords do not match",
                onAction: null,
            });
            return;
        }

        const payload = {
            polNo: formData.polNo,
            entityName: formData.entityName,
            state: formData.state,
            zip: formData.zip,
            fullTIN: formData.fullTIN,
            userName: formData.username,
            email: formData.email,
            password: formData.password,
        };

        try {
            const res = await fetch(`${apiURL}/account/register-entity`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            //if (res.status === 401) {
            //    logout();
            //    navigate('/login');
            //    throw new Error("unauthorized");
            //}

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            const data = await res.json();

            if (res.ok) {
                setModalData({
                    show: true,
                    message: data.message || "Registration successful!",
                    onAction: () => navigate("/login"),
                });
            } else {
                setModalData({
                    show: true,
                    message: data.message || "Registration failed",
                    onAction: null,
                });
            }
        } catch (err) {
            console.error("Registration error:", err);
            setModalData({
                show: true,
                message: "Something went wrong. Please try again.",
                onAction: null,
            });
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <AuthHeader portalSettings={portalSettings} />
                <h2 className="baseHeader">Register Trust or Entity</h2>
                <form className="baseForm" onSubmit={handleSubmit}>

                    <label className="baseLabel">Policy</label>
                    <input
                        type="text"
                        name="polNo"
                        className="baseInput"
                        value={formData.polNo}
                        onChange={handleChange}
                        maxLength="10"
                        pattern="[A-Za-z0-9]{10}"
                        required
                    />

                    <label className="baseLabel">Trust or Entity Name</label>
                    <input
                        type="text"
                        name="entityName"
                        className="baseInput"
                        value={formData.entityName}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">State</label>
                    <USStatesDropdown
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Zip</label>
                    <input
                        type="text"
                        name="zip"
                        className="baseInput"
                        value={formData.zip}
                        onChange={handleChange}
                        maxLength="5"
                        pattern="\d{5}"
                        required
                    />

                    <label className="baseLabel">Username</label>
                    <input
                        type="text"
                        name="username"
                        className="baseInput"
                        value={formData.username}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Email</label>
                    <input
                        type="email"
                        name="email"
                        className="baseInput"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Confirm Email</label>
                    <input
                        type="email"
                        name="confirmEmail"
                        className="baseInput"
                        value={formData.confirmEmail}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Full TIN</label>
                    <input
                        type="text"
                        name="fullTIN"
                        className="baseInput"
                        value={formData.fullTIN}
                        onChange={handleChange}
                        maxLength="9"
                        pattern="\d{9}"
                        required
                    />

                    <label className="baseLabel">Password</label>
                    <input
                        type="password"
                        name="password"
                        className="baseInput"
                        value={formData.password}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Confirm Password</label>
                    <input
                        type="password"
                        name="confirmPassword"
                        className="baseInput"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                    />

                    <button className="baseButton" type="submit">Register</button>
                </form>

                <div className="auth-links">
                    <Link to="/login">Back to Login</Link>

                    {psm.hasAgentPortal && (
                        <Link to="/registerAgent">Register Agent</Link>
                    )}

                    {psm.hasPolicyPortal && (
                        <Link to="/register">Register User</Link>
                    )}
                </div>
            </div>

            <Modal
                show={modalData.show}
                message={modalData.message}
                onClose={() => setModalData({ ...modalData, show: false })}
                onAction={modalData.onAction}
                actionLabel="Go to Login"
            />
        </div>
    );
};

export default RegisterEntityPage;
